/**
 * Short-lived in-memory list query cache for useEnterpriseListController.
 * Not an auth/permission layer. Cleared on authenticated user boundary.
 */

/** @typedef {{ items: any[], total: number, page?: number, pageSize?: number, fetchedAt: number, key: string }} ListCacheEntry */

export const LIST_STALE_MS = Object.freeze({
  service: 30_000,
  kunden: 5 * 60_000,
  maengel: 30_000,
  offerte: 60_000,
  inventar: 2 * 60_000,
  archiv: 60_000,
  "projekt:elektro": 60_000,
  "projekt:pv": 60_000,
  "projekt:sicherheit": 60_000,
  "projekt:ladestation": 60_000,
  "projekt:intern": 60_000,
  "projekt:ladenbau": 60_000,
  default: 30_000,
});

const MAX_ENTRIES = 40;

/** @type {Map<string, ListCacheEntry>} */
const store = new Map();

function touch(key, entry) {
  // LRU: re-insert to move to end
  store.delete(key);
  store.set(key, entry);
  while (store.size > MAX_ENTRIES) {
    const oldest = store.keys().next().value;
    store.delete(oldest);
  }
}

export function getListStaleMs(domain) {
  if (domain && LIST_STALE_MS[domain] != null) return LIST_STALE_MS[domain];
  if (domain && String(domain).startsWith("projekt:")) return 60_000;
  return LIST_STALE_MS.default;
}

/**
 * @param {string} key
 * @param {{ staleTime?: number, now?: number }} [opts]
 * @returns {{ entry: ListCacheEntry|null, fresh: boolean, stale: boolean }}
 */
export function getListCache(key, opts = {}) {
  if (!key) return { entry: null, fresh: false, stale: false };
  const entry = store.get(key) || null;
  if (!entry) return { entry: null, fresh: false, stale: false };
  touch(key, entry);
  const staleTime = opts.staleTime ?? LIST_STALE_MS.default;
  const now = opts.now ?? Date.now();
  const age = now - entry.fetchedAt;
  const fresh = age <= staleTime;
  return { entry, fresh, stale: !fresh };
}

/**
 * @param {string} key
 * @param {{ items: any[], total: number, page?: number, pageSize?: number }} data
 */
export function setListCache(key, data) {
  if (!key || !data) return;
  const entry = {
    key,
    items: Array.isArray(data.items) ? data.items : [],
    total: Number(data.total) || 0,
    page: data.page,
    pageSize: data.pageSize,
    fetchedAt: Date.now(),
  };
  touch(key, entry);
  return entry;
}

export function deleteListCache(key) {
  if (!key) return false;
  return store.delete(key);
}

/**
 * Invalidate by exact key or prefix (domain).
 * @param {string} keyOrPrefix
 * @param {{ prefix?: boolean }} [opts]
 */
export function invalidateListCache(keyOrPrefix, opts = {}) {
  if (!keyOrPrefix) return 0;
  if (!opts.prefix) {
    return store.delete(keyOrPrefix) ? 1 : 0;
  }
  let n = 0;
  for (const key of [...store.keys()]) {
    if (key.startsWith(keyOrPrefix)) {
      store.delete(key);
      n += 1;
    }
  }
  return n;
}

/** Invalidate all entries for a domain, e.g. "service" or "projekt:elektro". */
export function invalidateListDomain(domain) {
  if (!domain) return 0;
  return invalidateListCache(`${domain}:`, { prefix: true });
}

export function clearAllListCache() {
  const n = store.size;
  store.clear();
  return n;
}

/** Test / debug helper */
export function __listCacheSize() {
  return store.size;
}

/** Test helper */
export function __listCacheKeys() {
  return [...store.keys()];
}

// E2E / debug seam (not an auth surface — cache is UX only)
if (typeof window !== "undefined") {
  window.__WT_LIST_CACHE__ = {
    invalidateListDomain,
    clearAllListCache,
    getListCache,
    setListCache,
    size: __listCacheSize,
  };
}
